import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SoftwareCard from "@/components/base/SoftwareCard";
import usePageMeta from "@/hooks/usePageMeta";
import { getCategoryById, getRelatedSoftware, getSoftwareById, buildVersionHistory, pickComments } from "@/utils/software";
import SoftwareHeader from "./components/SoftwareHeader";
import VersionHistory from "./components/VersionHistory";
import CommentSection from "./components/CommentSection";

export default function DetailPage() {
  const { softwareId = "" } = useParams();
  const software = getSoftwareById(softwareId);
  const category = software ? getCategoryById(software.categoryId) : undefined;

  usePageMeta(
    software ? `${software.name}最新版下载及介绍 - 最近最新手机中文大全10` : "软件详情 - 最近最新手机中文大全10",
    software
      ? `${software.name} v${software.version}，${software.shortDesc} 大小 ${software.size}，更新时间 ${software.updatedAt}，支持安卓与 iOS 用户下载了解。`
      : "",
  );

  if (!software) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 w-full max-w-[1240px] mx-auto px-4 md:px-6 pt-28 pb-20">
          <div className="bg-background-100 rounded-lg border border-background-200 p-10 text-center">
            <span className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-background-200 text-secondary-500 text-3xl">
              <i className="ri-emotion-sad-line" aria-hidden="true"></i>
            </span>
            <h1 className="mt-4 font-heading text-lg font-bold text-foreground-950">未找到该软件</h1>
            <p className="mt-2 text-sm text-foreground-600">该软件可能已下架或链接有误，请返回首页浏览其他应用。</p>
            <Link to="/" className="mt-5 inline-flex items-center gap-2 h-10 px-5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const versions = buildVersionHistory(software);
  const comments = pickComments(software.id, 4);
  const related = getRelatedSoftware(software, 4);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-16">
        <SoftwareHeader
          software={software}
          topSlot={
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: category ? `${category.name}软件` : "软件分类", to: category ? `/category/${category.id}` : "/" },
                { label: software.name },
              ]}
            />
          }
        />

        <div className="w-full max-w-[1240px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 min-w-0 space-y-8">
              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">软件介绍</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{software.description}</p>
              </section>

              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">主要特色</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {software.features.map((f) => (
                    <div key={f} className="flex items-start gap-2.5 bg-background-100 rounded-lg border border-background-200 p-4">
                      <span className="flex items-center justify-center w-5 h-5 text-accent-500 shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-lg" aria-hidden="true"></i>
                      </span>
                      <span className="text-sm text-foreground-700 leading-relaxed">{f}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">应用截图</h2>
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {software.screenshots.map((shot, i) => (
                    <div key={shot} className="w-[180px] h-[360px] md:w-[200px] md:h-[400px] rounded-lg overflow-hidden bg-background-100 border border-background-200 shrink-0">
                      <img src={shot} alt={`${software.name} 应用截图 ${i + 1}`} title={`${software.name} 界面截图`} className="w-full h-full object-cover object-top" />
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">版本更新历史</h2>
                <VersionHistory versions={versions} />
              </section>

              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">用户评论</h2>
                <CommentSection comments={comments} />
              </section>
            </div>

            <aside className="lg:w-[320px] shrink-0 space-y-6">
              <div className="bg-background-100 rounded-lg border border-background-200 p-5 lg:sticky lg:top-20">
                <h3 className="font-heading text-base font-bold text-foreground-950">软件信息</h3>
                <ul className="mt-4 space-y-3 text-sm">
                  {[
                    { k: "软件名称", v: software.name },
                    { k: "所属分类", v: category ? category.name : "-" },
                    { k: "当前版本", v: `v${software.version}` },
                    { k: "软件大小", v: software.size },
                    { k: "更新时间", v: software.updatedAt },
                    { k: "支持平台", v: software.platforms.map((p) => (p === "ios" ? "iOS" : "安卓")).join(" / ") },
                    { k: "开发者", v: software.developer },
                  ].map((row) => (
                    <li key={row.k} className="flex items-center justify-between gap-3 pb-3 border-b border-background-200 last:border-0 last:pb-0">
                      <span className="text-secondary-500 shrink-0">{row.k}</span>
                      <span className="text-foreground-900 text-right truncate">{row.v}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {software.tags.map((t) => (
                    <span key={t} className="text-xs text-secondary-600 bg-secondary-100 rounded-full px-2.5 py-1">{t}</span>
                  ))}
                </div>
                <a
                  href={software.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 flex items-center justify-center gap-2 h-11 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="flex items-center justify-center w-4 h-4"><i className="ri-download-cloud-line text-lg" aria-hidden="true"></i></span>
                  前往下载
                </a>
              </div>
            </aside>
          </div>

          <section className="mt-12">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">相关推荐软件</h2>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {related.map((item, i) => (
                <SoftwareCard key={item.id} software={item} index={i} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background-50/95 backdrop-blur border-t border-background-200 px-4 py-3">
        <a
          href={software.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 h-11 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="flex items-center justify-center w-4 h-4"><i className="ri-download-cloud-line text-lg" aria-hidden="true"></i></span>
          立即下载 {software.name} 最新版
        </a>
      </div>
      <div className="lg:hidden h-20"></div>
    </div>
  );
}